import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ArrowLeft, Loader2, Share2, Check, Clock, Trash2, MessageSquare } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

export default function TripChat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const substanceSlug = searchParams.get('substance');
  const personaSlug = searchParams.get('persona');
  const topicParam = searchParams.get('topic') || '';
  const chatIdParam = searchParams.get('chatId');

  const [substance, setSubstance] = useState(null);
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Persistence state
  const [chatId, setChatId] = useState(chatIdParam || null);
  const [savedChats, setSavedChats] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const saveTimerRef = useRef(null);

  // Fetch substance and persona on mount
  useEffect(() => {
    if (!substanceSlug || !personaSlug) {
      navigate('/altered-states');
      return;
    }

    const promises = [
      API.get(`/api/altered-states/${substanceSlug}`),
      API.get(`/api/personas/${personaSlug}`),
    ];

    // If loading a saved chat, fetch it too
    if (chatIdParam) {
      promises.push(API.get(`/api/trip-chats/${chatIdParam}`));
    }

    Promise.all(promises)
      .then((results) => {
        setSubstance(results[0].data);
        setPersona(results[1].data);
        if (results[2]?.data) {
          setMessages(results[2].data.messages || []);
          setChatId(results[2].data.id);
          setShareToken(results[2].data.share_token || null);
        }
      })
      .catch(() => {
        setError('Could not load substance or persona');
      })
      .finally(() => setLoading(false));
  }, [substanceSlug, personaSlug, chatIdParam, navigate]);

  // Load saved chats list
  useEffect(() => {
    if (user) {
      API.get('/api/trip-chats')
        .then(res => setSavedChats(res.data.chats || []))
        .catch(() => {});
    }
  }, [user]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when ready
  useEffect(() => {
    if (!loading) inputRef.current?.focus();
  }, [loading]);

  // Auto-save after messages change (debounced)
  const saveChat = useCallback(async (msgs, currentChatId) => {
    if (!msgs.length || !substanceSlug || !personaSlug) return;
    setSaving(true);
    try {
      const res = await API.post('/api/trip-chats', {
        id: currentChatId || undefined,
        substance_slug: substanceSlug,
        persona_slug: personaSlug,
        topic: topicParam || undefined,
        messages: msgs,
      });
      const newId = res.data.id;
      if (!currentChatId && newId) {
        setChatId(newId);
        // Update URL with chatId without losing other params
        const newParams = new URLSearchParams(searchParams);
        newParams.set('chatId', newId);
        setSearchParams(newParams, { replace: true });
      }
      // Refresh saved chats list
      API.get('/api/trip-chats')
        .then(r => setSavedChats(r.data.chats || []))
        .catch(() => {});
    } catch {
      // Silent fail on auto-save
    }
    setSaving(false);
  }, [substanceSlug, personaSlug, topicParam, searchParams, setSearchParams]);

  useEffect(() => {
    if (messages.length === 0) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveChat(messages, chatId);
    }, 1500);
    return () => clearTimeout(saveTimerRef.current);
  }, [messages, chatId, saveChat]);

  // Build the system prompt that combines persona + substance + topic
  const buildSystemPrompt = () => {
    if (!persona || !substance) return '';
    const parts = [];
    if (persona.system_prompt) parts.push(persona.system_prompt);
    if (substance.system_prompt_injection) parts.push(substance.system_prompt_injection);
    parts.push(
      `You are ${persona.name} having a one-on-one conversation. ` +
      `You are currently experiencing ${substance.name}. ` +
      (topicParam ? `The user wants to discuss: ${topicParam}. ` : '') +
      `Stay in character. Be conversational, not formal. ` +
      `Let the ${substance.name} influence your tone, associations, and personality.`
    );
    return parts.join('\n\n');
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;

    const userMsg = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setSending(true);
    setError('');

    try {
      const systemPrompt = buildSystemPrompt();
      const res = await API.post('/api/prompt-builder/chat', {
        message: userMsg.content,
        history: newMessages.map(m => ({ role: m.role, content: m.content })),
        context_items: [
          { title: 'System Instructions', type: 'system', snippet: systemPrompt },
          { title: `Altered State: ${substance.name}`, type: 'state', snippet: `${substance.emoji} ${substance.name} - ${substance.tagline}` },
        ],
      });
      const responseText = (res.data.response || '').replace(/<!--\s*SPEC_STATE[\s\S]*?-->/, '').trim();
      setMessages(prev => [...prev, { role: 'assistant', content: responseText }]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get response');
    }
    setSending(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleShare = async () => {
    if (!chatId) return;
    try {
      const res = await API.post(`/api/trip-chats/${chatId}/share`);
      const token = res.data.share_token;
      setShareToken(token);
      const url = `${window.location.origin}/trip-chat/shared/${token}`;
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      setError('Failed to generate share link');
    }
  };

  const handleDeleteChat = async (id, e) => {
    e?.stopPropagation();
    try {
      await API.delete(`/api/trip-chats/${id}`);
      setSavedChats(prev => prev.filter(c => c.id !== id));
      if (id === chatId) {
        setChatId(null);
        setMessages([]);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('chatId');
        setSearchParams(newParams, { replace: true });
      }
    } catch {
      setError('Failed to delete chat');
    }
  };

  const loadSavedChat = (chat) => {
    navigate(`/trip-chat?substance=${chat.substance_slug}&persona=${chat.persona_slug}${chat.topic ? `&topic=${encodeURIComponent(chat.topic)}` : ''}&chatId=${chat.id}`);
    setShowHistory(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F]">
        <Header />
        <div className="pt-20 flex items-center justify-center h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#A855F7] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-mono text-[#A0A0A0]">Loading trip...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !substance) {
    return (
      <div className="min-h-screen bg-[#0F0F0F]">
        <Header />
        <div className="pt-20 flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <p className="text-[#EF4444] mb-4">{error}</p>
            <button onClick={() => navigate('/altered-states')} className="px-4 py-2 bg-[#A855F7] text-white text-sm">
              Back to Altered States
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col">
      <Header />

      <div className="pt-14 flex-1 flex flex-col lg:flex-row">
        {/* Left sidebar: substance + persona info */}
        <div className="lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-[#2A2A2A] p-4 lg:p-6">
          <button
            onClick={() => navigate('/altered-states')}
            className="flex items-center gap-1 text-xs text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors mb-4"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>

          {substance && (
            <div className="mb-6">
              <span className="text-4xl block mb-2">{substance.emoji}</span>
              <h3 className="text-sm font-bold font-['Barlow_Condensed'] text-[#A855F7]">{substance.name}</h3>
              <p className="text-[10px] text-[#A0A0A0] mt-1">{substance.tagline}</p>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map(lvl => (
                  <div key={lvl} className={`h-1 flex-1 ${lvl <= Math.ceil((substance.intensity || 5) / 2) ? 'bg-[#A855F7]' : 'bg-[#2A2A2A]'}`} />
                ))}
              </div>
            </div>
          )}

          {persona && (
            <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center border text-[9px] font-bold font-['Barlow_Condensed']"
                  style={{ borderColor: persona.color || '#A855F7', color: persona.color || '#A855F7' }}>
                  {persona.name.charAt(0)}
                </div>
                <span className="text-sm font-medium font-['Barlow_Condensed']" style={{ color: persona.color || '#F5F5F5' }}>
                  {persona.name}
                </span>
              </div>
              {persona.description && (
                <p className="text-[10px] text-[#A0A0A0] leading-relaxed">{persona.description}</p>
              )}
              <div className="mt-2 text-[9px] font-mono text-[#A855F7]">
                Currently on: {substance?.emoji} {substance?.name}
              </div>
            </div>
          )}

          {topicParam && (
            <div className="mt-4 p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
              <span className="text-[9px] font-mono text-[#A0A0A0] block mb-1">TOPIC</span>
              <p className="text-xs text-[#F5F5F5]">{topicParam}</p>
            </div>
          )}

          {/* Actions: Share + History */}
          <div className="mt-4 flex flex-col gap-2">
            {chatId && (
              <button
                onClick={handleShare}
                className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-xs text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#A855F7] transition-colors"
              >
                {shareCopied ? <><Check className="w-3 h-3 text-green-400" /> Link copied!</> : <><Share2 className="w-3 h-3" /> Share this trip</>}
              </button>
            )}
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-xs text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#A855F7] transition-colors"
            >
              <Clock className="w-3 h-3" /> My trip chats {savedChats.length > 0 && `(${savedChats.length})`}
            </button>
          </div>

          {/* Save indicator */}
          {saving && (
            <div className="mt-2 flex items-center gap-1 text-[9px] font-mono text-[#A0A0A0]">
              <Loader2 className="w-2.5 h-2.5 animate-spin" /> saving...
            </div>
          )}

          {/* Saved chats history panel */}
          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-3 overflow-hidden"
              >
                <div className="max-h-60 overflow-y-auto space-y-1.5">
                  {savedChats.length === 0 ? (
                    <p className="text-[10px] text-[#A0A0A0] p-2">No saved trips yet</p>
                  ) : (
                    savedChats.map(chat => (
                      <div
                        key={chat.id}
                        onClick={() => loadSavedChat(chat)}
                        className={`p-2 cursor-pointer border transition-colors group ${
                          chat.id === chatId
                            ? 'bg-[#A855F7]/10 border-[#A855F7]/30'
                            : 'bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#A855F7]/50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-[#F5F5F5] truncate">{chat.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[9px] font-mono text-[#A0A0A0]">
                                <MessageSquare className="w-2.5 h-2.5 inline mr-0.5" />{chat.message_count}
                              </span>
                              <span className="text-[9px] font-mono text-[#A0A0A0]">
                                {new Date(chat.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => handleDeleteChat(chat.id, e)}
                            className="p-1 opacity-0 group-hover:opacity-100 text-[#A0A0A0] hover:text-[#EF4444] transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right: Chat interface */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-md">
                  <span className="text-4xl block mb-3">{substance?.emoji}</span>
                  <p className="text-sm text-[#A0A0A0]">
                    Start chatting with {persona?.name} while they're on {substance?.name}.
                    {topicParam ? ` Topic: "${topicParam}"` : ' Say anything to begin.'}
                  </p>
                </div>
              </div>
            )}

            {messages.map((msg, i) => {
              const isUser = msg.role === 'user';
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] p-3 ${
                    isUser
                      ? 'bg-[#FF4D00]/10 border border-[#FF4D00]/30 ml-auto'
                      : 'bg-[#1A1A1A] border border-[#2A2A2A]'
                  }`}>
                    {!isUser && (
                      <span className="text-[10px] font-mono text-[#A855F7] block mb-1">
                        {persona?.name} {substance?.emoji}
                      </span>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </motion.div>
              );
            })}

            {sending && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                  <span className="text-[10px] font-mono text-[#A855F7] block mb-1">
                    {persona?.name} {substance?.emoji}
                  </span>
                  <div className="flex items-center gap-2 text-sm text-[#A0A0A0]">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span className="font-mono text-[10px]">tripping...</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error */}
          {error && (
            <div className="mx-4 lg:mx-6 mb-2 p-2 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs">
              {error}
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 lg:p-6 border-t border-[#2A2A2A] shrink-0" data-testid="chat-input-form">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                className="flex-1 px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#A855F7] transition-colors"
                placeholder={`Say something to ${persona?.name || 'the persona'}...`}
                disabled={sending}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="px-4 py-3 bg-[#A855F7] text-white hover:bg-[#A855F7]/80 transition-colors disabled:opacity-40"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
